/**
 * useGlobalReservationSync.ts
 *
 * アプリケーション全体での予約データ同期管理用フック
 * App.jsなどで使用し、コンポーネントのマウント状況に関わらず同期を継続させる
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { reservationSyncService } from '../services/reservationSyncService';
import { vehicleSyncService } from '../services/vehicleSyncService';
import { useAuth } from '../components/AuthContext';

const POLLING_COOKIE = 'calv2_polling_enabled';

function getPollingCookie(): boolean {
    try {
        const raw = document.cookie.split('; ').find(c => c.startsWith(`${POLLING_COOKIE}=`));
        return raw ? raw.split('=')[1] === 'true' : false;
    } catch { return false; }
}

function setPollingCookie(enabled: boolean): void {
    document.cookie = `${POLLING_COOKIE}=${enabled}; path=/; max-age=${60 * 60 * 24 * 365}`;
}

export function useGlobalReservationSync(enabled: boolean = true) {
    const [pollingEnabled, setPollingEnabled] = useState<boolean>(getPollingCookie);
    const { token, softLoggedOut } = useAuth();
    const lastTokenRef = useRef<string | null>(null);

    const togglePolling = useCallback(() => {
        setPollingEnabled(prev => {
            const next = !prev;
            setPollingCookie(next);
            return next;
        });
    }, []);

    // 初回データ取得 & 再ログイン検知
    // - 初回: startOnce で認証付きの初回同期
    // - 再ログイン（token が変わった）: forceSync で強制再同期
    // - トークン復活（null → 値あり）: forceSync で再同期
    //   ソフトログアウト中は token=null、Provider は unmount しないため、
    //   token の変化を watch して明示的に再同期する必要がある。
    useEffect(() => {
        if (!enabled || softLoggedOut || !token) {
            return;
        }

        const prev = lastTokenRef.current;
        lastTokenRef.current = token;

        if (prev === null && !reservationSyncService.hasData()) {
            // 完全初回（未ログインからログイン）
            console.log('[GlobalSync] Initial sync on login');
            reservationSyncService.startOnce();
            vehicleSyncService.startOnce();
        } else if (prev !== null && prev !== token) {
            // トークンが変わった = ソフトログアウトからの再ログイン
            console.log('[GlobalSync] Token changed (re-login) — forcing resync');
            reservationSyncService.forceSync();
            vehicleSyncService.forceSync();
        } else if (!reservationSyncService.hasData() || !vehicleSyncService.hasData()) {
            // トークンは同じだがデータが空（過去の同期失敗等）→ 復旧のため再同期
            console.log('[GlobalSync] Cache empty — forcing resync');
            reservationSyncService.forceSync();
            vehicleSyncService.forceSync();
        }
    }, [enabled, token, softLoggedOut]);

    // unmount 時の停止（ハードログアウト等）
    useEffect(() => {
        if (!enabled) return;
        return () => {
            reservationSyncService.stop();
            vehicleSyncService.stop();
            lastTokenRef.current = null;
        };
    }, [enabled]);

    // ポーリング制御（トグルで即時切り替え）
    useEffect(() => {
        if (!enabled) return;
        if (pollingEnabled) {
            reservationSyncService.startPolling();
            vehicleSyncService.startPolling();
            console.log('[GlobalSync] Polling ON');
        } else {
            reservationSyncService.stopPolling();
            vehicleSyncService.stopPolling();
            console.log('[GlobalSync] Polling OFF');
        }
        return () => {
            reservationSyncService.stopPolling();
            vehicleSyncService.stopPolling();
        };
    }, [enabled, pollingEnabled]);

    return { pollingEnabled, togglePolling };
}

